import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faDocker } from '@fortawesome/free-brands-svg-icons';
import { useContainers } from '../hooks/useContainers';
import ContainerCard from './ContainerCard';
import ContainerDetails from './ContainerDetails';

/**
 * Docker Component
 * Main page for Docker container management
 */
export default function Docker() {
  const { containers, loading, refresh } = useContainers();
  const [filter, setFilter] = useState('all'); // all, running, stopped
  const [selectedContainer, setSelectedContainer] = useState(null);

  // Filter containers by state
  const filteredContainers = containers.filter(c => {
    if (filter === 'running') return c.state === 'running';
    if (filter === 'stopped') return c.state !== 'running';
    return true;
  });

  // Auto-refresh every 5 seconds
  useEffect(() => {
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [refresh]);

  // Telegram BackButton handling
  useEffect(() => {
    if (selectedContainer && window.Telegram?.WebApp?.BackButton) {
      const handleBack = () => setSelectedContainer(null);
      window.Telegram.WebApp.BackButton.onClick(handleBack);
      window.Telegram.WebApp.BackButton.show();
      return () => {
        window.Telegram.WebApp.BackButton.offClick(handleBack);
        window.Telegram.WebApp.BackButton.hide();
      };
    }
  }, [selectedContainer]);

  const handleActionComplete = () => {
    // Refresh container list after action
    setTimeout(refresh, 1000);
  };

  // Show details view if container is selected
  if (selectedContainer) {
    return (
      <ContainerDetails
        container={selectedContainer}
        onClose={() => setSelectedContainer(null)}
        onUpdate={handleActionComplete}
      />
    );
  }

  return (
    <div className="flex flex-col h-full bg-theme-bg-color">
      {/* Header */}
      <div className="bg-theme-secondary-bg-color border-b border-theme-section-separator-color">
        <div className="flex items-center justify-between px-3 sm:px-5 py-3 sm:py-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-theme-text-color flex items-center gap-2 sm:gap-3">
            <FontAwesomeIcon icon={faDocker} className="text-blue-500" />
            Docker
          </h1>
          <button
            onClick={refresh}
            disabled={loading}
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-theme-button-color to-theme-button-color shadow-lg text-theme-button-text-color flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-50"
          >
            <FontAwesomeIcon
              icon={faRotate}
              className={loading ? 'animate-spin' : ''}
            />
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 px-3 sm:px-5 pb-3 sm:pb-4">
          {['all', 'running', 'stopped'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`flex-1 px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl font-medium transition-all text-sm sm:text-base ${
                filter === f
                  ? 'bg-theme-button-color text-theme-button-text-color shadow-md'
                  : 'text-theme-hint-color hover:bg-theme-bg-color'
              }`}
            >
              {f === 'all' ? 'Все' : f === 'running' ? 'Запущены' : 'Остановлены'}
            </button>
          ))}
        </div>
      </div>

      {/* Container List */}
      <div className="flex-1 overflow-y-auto px-2 sm:px-3 py-2 space-y-2 pb-28">
        {loading && containers.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-theme-button-color" />
          </div>
        ) : filteredContainers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-theme-hint-color">
            <FontAwesomeIcon icon={faDocker} size="3x" className="opacity-40 mb-4" />
            <p>Нет контейнеров</p>
            {filter !== 'all' && (
              <button
                onClick={() => setFilter('all')}
                className="mt-4 px-4 py-2 rounded-lg bg-theme-button-color text-theme-button-text-color"
              >
                Показать все
              </button>
            )}
          </div>
        ) : (
          filteredContainers.map(container => (
            <ContainerCard
              key={container.id}
              container={container}
              onClick={() => setSelectedContainer(container)}
              onActionComplete={handleActionComplete}
            />
          ))
        )}
      </div>
    </div>
  );
}
